import * as THREE from "three";
import {
  createPositionGizmo,
  disposePositionGizmo,
  getAxisWorldDirection,
  setDraggingAxis,
  setHoveredAxis,
  updateGizmoTransform,
  type AxisIndex,
  type PositionGizmo,
} from "./positionGizmoObject";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { getHistoryActions } from "../stores/historyStore";
import { logger } from "../utils/logger";
import type { BodyUUID } from "../types/primitives";

type MaybeRef<T> = { current: T | null | undefined } | null | undefined;

type ScaleTarget =
  | {
      kind: "mesh";
      meshUuid: string;
      bodyUuid: string;
      object?: THREE.Object3D | null;
    }
  | {
      kind: "geom";
      bodyUuid: string;
      geomId: string;
      geomKind: string;
      meshUuid?: string | null;
      object?: THREE.Object3D | null;
    };

export type AttachScaleGizmoOptions = {
  sceneRef: MaybeRef<THREE.Scene>;
  cameraRef: MaybeRef<THREE.Camera>;
  rendererRef: MaybeRef<THREE.WebGLRenderer>;
  controlsRef: MaybeRef<{ enabled: boolean }>;
  getSelectedTarget: () => ScaleTarget | null;
  isDisabled: () => boolean;
  isOrbiting: () => boolean;
};

export type ScaleGizmoController = {
  frameUpdate: () => void;
  detach: () => void;
};

const MIN_SCALE_VALUE = 1e-6;
type SceneGeomEntry = {
  uuid?: string;
  kind?: string;
  mesh?: string;
  size?: unknown;
};
type SceneBodyLookup = Record<string, { geoms?: SceneGeomEntry[] } | undefined>;
type SceneMeshLookup = Record<string, { scale?: unknown } | undefined>;

function closestPointParamOnLineToRay(
  lineOrigin: THREE.Vector3,
  lineDir: THREE.Vector3,
  rayOrigin: THREE.Vector3,
  rayDir: THREE.Vector3
): number {
  const w0 = new THREE.Vector3().subVectors(lineOrigin, rayOrigin);
  const a = lineDir.dot(lineDir);
  const b = lineDir.dot(rayDir);
  const c = rayDir.dot(rayDir);
  const d = lineDir.dot(w0);
  const e = rayDir.dot(w0);
  const denom = a * c - b * b;
  if (Math.abs(denom) < 1e-9) return -d / (a || 1);
  return (b * e - c * d) / denom;
}

function setNdcPointerFromEvent(
  pointer: THREE.Vector2,
  event: PointerEvent,
  rendererDom: HTMLElement
): void {
  const rect = rendererDom.getBoundingClientRect();
  pointer.set(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -(((event.clientY - rect.top) / rect.height) * 2 - 1)
  );
}

function computeWorldMatrix(obj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [obj];
  const seen = new Set<THREE.Object3D>([obj]);
  let cur: THREE.Object3D | null = obj.parent || null;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const node = chain[i];
    node.updateMatrix?.();
    if (node.matrix) world.multiply(node.matrix);
  }
  return world;
}

function computeVisibleGeometryWorldCenter(
  root: THREE.Object3D,
  fallback: THREE.Vector3
): THREE.Vector3 {
  const bounds = new THREE.Box3();
  const stack: THREE.Object3D[] = [root];
  const seen = new Set<THREE.Object3D>();

  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    if (!obj.visible) continue;

    const geometry = (obj as THREE.Object3D & { geometry?: THREE.BufferGeometry }).geometry;
    const localBounds = geometry?.boundingBox || null;
    if (geometry && !localBounds) {
      geometry.computeBoundingBox();
    }
    if (geometry?.boundingBox) {
      const worldBounds = geometry.boundingBox.clone().applyMatrix4(computeWorldMatrix(obj));
      if (!worldBounds.isEmpty()) {
        bounds.union(worldBounds);
      }
    }

    for (let i = obj.children.length - 1; i >= 0; i -= 1) {
      stack.push(obj.children[i]);
    }
  }

  return bounds.isEmpty() ? fallback : bounds.getCenter(new THREE.Vector3());
}

function findAncestorForBodyUuid(obj: THREE.Object3D, bodyUuid: string): THREE.Object3D | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    if (String(cur.userData?.bodyUuid || "") === String(bodyUuid)) {
      return cur;
    }
    cur = cur.parent;
  }
  return null;
}

function getCompleteSize(size: unknown): [number, number, number] {
  const source = Array.isArray(size) ? size : [];
  const s0 = Number(source[0] ?? 0.1);
  const s1 = Number(source[1] ?? s0);
  const s2 = Number(source[2] ?? s1);
  return [
    Number.isFinite(s0) ? s0 : 0.1,
    Number.isFinite(s1) ? s1 : 0.1,
    Number.isFinite(s2) ? s2 : 0.1,
  ];
}

function axisToGeomSizeIndex(geomKind: string, axis: AxisIndex): number {
  const kind = String(geomKind || "").toLowerCase();
  if (kind === "cylinder" || kind === "capsule") return axis === 2 ? 1 : 0;
  return axis;
}

function applyGeomScale(
  target: Extract<ScaleTarget, { kind: "geom" }>,
  axis: AxisIndex,
  nextAxisValue: number
): void {
  const state = getSceneState();
  const body = (state.bodies as SceneBodyLookup | undefined)?.[target.bodyUuid];
  const geom = Array.isArray(body?.geoms)
    ? body.geoms.find((entry) => String(entry?.uuid) === String(target.geomId))
    : null;
  if (!geom || geom.mesh) return;

  const kind = String(geom.kind || "sphere").toLowerCase();
  const nextSize = getCompleteSize(geom.size);
  const clamped = Math.max(MIN_SCALE_VALUE, nextAxisValue);

  if (kind === "sphere") {
    nextSize[0] = clamped;
    nextSize[1] = clamped;
    nextSize[2] = clamped;
  } else {
    nextSize[axisToGeomSizeIndex(kind, axis)] = clamped;
  }

  getSceneActions().setGeomSize(target.bodyUuid as BodyUUID, target.geomId, nextSize);
  try {
    window.dispatchEvent(new CustomEvent("structure-changed"));
  } catch (error) {
    logger.warn("[ScaleGizmo] Failed to dispatch structure-changed after visual geom scale", error);
  }
}

export function attachScaleGizmoController(opts: AttachScaleGizmoOptions): ScaleGizmoController {
  const { sceneRef, cameraRef, rendererRef, controlsRef, isDisabled, isOrbiting } = opts;
  const scene = sceneRef?.current;
  const renderer = rendererRef?.current;
  const camera = cameraRef?.current;
  if (!scene || !renderer || !camera) return { frameUpdate: () => {}, detach: () => {} };

  const gizmo: PositionGizmo = createPositionGizmo();
  gizmo.root.name = "__scaleGizmoRoot";
  gizmo.root.userData.isScaleGizmo = true;
  scene.add(gizmo.root);

  const raycaster = new THREE.Raycaster();
  raycaster.layers.enableAll();
  const pointer = new THREE.Vector2();
  const rendererDom = renderer.domElement;

  let hoveredAxis: AxisIndex | null = null;
  let dragState: {
    axis: AxisIndex;
    target: ScaleTarget;
    startValues: [number, number, number];
    axisOriginWorld: THREE.Vector3;
    axisDirWorld: THREE.Vector3;
    startParam: number;
    startedHistoryGroup: boolean;
    pointerId: number | null;
  } | null = null;

  const updateHover = (event: PointerEvent): AxisIndex | null => {
    if (!gizmo.root.visible) return null;
    setNdcPointerFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const intersects = raycaster.intersectObjects(gizmo.pickTargets, false);
    if (!intersects.length) return null;
    const axis = intersects[0].object.userData?.positionGizmoAxis;
    return typeof axis === "number" ? (axis as AxisIndex) : null;
  };

  const setHover = (axis: AxisIndex | null) => {
    if (hoveredAxis === axis) return;
    hoveredAxis = axis;
    setHoveredAxis(gizmo, axis);
    if (!dragState) rendererDom.style.cursor = axis == null ? "" : "grab";
  };

  const getTargetValues = (target: ScaleTarget): [number, number, number] | null => {
    const state = getSceneState();
    if (target.kind === "mesh") {
      const scale = (state.meshes as SceneMeshLookup | undefined)?.[target.meshUuid]?.scale;
      return Array.isArray(scale) && scale.length === 3 ? getCompleteSize(scale) : [1, 1, 1];
    }

    const body = (state.bodies as SceneBodyLookup | undefined)?.[target.bodyUuid];
    const geom = Array.isArray(body?.geoms)
      ? body.geoms.find((entry) => String(entry?.uuid) === String(target.geomId))
      : null;
    if (!geom) return null;
    if (geom.mesh) {
      const scale = (state.meshes as SceneMeshLookup | undefined)?.[String(geom.mesh)]?.scale;
      return Array.isArray(scale) && scale.length === 3 ? getCompleteSize(scale) : [1, 1, 1];
    }
    return getCompleteSize(geom.size);
  };

  const applyTargetValue = (target: ScaleTarget, axis: AxisIndex, nextValue: number) => {
    const clamped = Math.max(MIN_SCALE_VALUE, nextValue);
    if (target.kind === "mesh") {
      const current = getTargetValues(target) || [1, 1, 1];
      const next = [...current] as [number, number, number];
      next[axis] = clamped;
      getSceneActions().setMeshScale(target.meshUuid, next);
      return;
    }

    if (target.meshUuid) {
      const current = getTargetValues({
        kind: "mesh",
        meshUuid: target.meshUuid,
        bodyUuid: target.bodyUuid,
      }) || [1, 1, 1];
      const next = [...current] as [number, number, number];
      next[axis] = clamped;
      getSceneActions().setMeshScale(target.meshUuid, next);
      return;
    }

    applyGeomScale(target, axis, clamped);
  };

  const onPointerMoveCanvas = (event: PointerEvent) => {
    if (dragState) return;
    if (isOrbiting() || isDisabled() || !gizmo.root.visible) {
      setHover(null);
      return;
    }
    setHover(updateHover(event));
  };

  const onPointerLeaveCanvas = () => {
    if (!dragState) setHover(null);
  };

  const onPointerDownWindow = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const eventTarget = event.target as Node | null;
    if (!eventTarget || (eventTarget !== rendererDom && !rendererDom.contains(eventTarget))) return;
    if (isDisabled() || !gizmo.root.visible) return;
    const axis = updateHover(event);
    if (axis == null) return;
    const target = opts.getSelectedTarget();
    if (!target) return;
    const startValues = getTargetValues(target);
    if (!startValues) return;

    gizmo.root.updateMatrixWorld(true);
    const axisOriginWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
    const axisDirWorld = getAxisWorldDirection(gizmo, axis, new THREE.Vector3());

    setNdcPointerFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const startParam = closestPointParamOnLineToRay(
      axisOriginWorld,
      axisDirWorld,
      raycaster.ray.origin,
      raycaster.ray.direction
    );

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    dragState = {
      axis,
      target,
      startValues,
      axisOriginWorld,
      axisDirWorld,
      startParam,
      startedHistoryGroup: false,
      pointerId: event.pointerId,
    };
    setDraggingAxis(gizmo, axis);
    try {
      getHistoryActions().beginGroup(getSceneState());
      dragState.startedHistoryGroup = true;
    } catch (error) {
      logger.warn("[ScaleGizmo] Failed to begin history group", error);
    }

    if (controlsRef?.current) controlsRef.current.enabled = false;
    rendererDom.style.cursor = "grabbing";
    try {
      rendererDom.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }

    window.addEventListener("pointermove", onPointerMoveWindow, true);
    window.addEventListener("pointerup", onPointerUpWindow, true);
    window.addEventListener("pointercancel", onPointerUpWindow, true);
  };

  const onPointerMoveWindow = (event: PointerEvent) => {
    if (!dragState) return;
    setNdcPointerFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const currentParam = closestPointParamOnLineToRay(
      dragState.axisOriginWorld,
      dragState.axisDirWorld,
      raycaster.ray.origin,
      raycaster.ray.direction
    );
    if (!Number.isFinite(currentParam)) return;
    const delta = currentParam - dragState.startParam;
    const valueIndex =
      dragState.target.kind === "geom"
        ? axisToGeomSizeIndex(dragState.target.geomKind, dragState.axis)
        : dragState.axis;
    const nextValue = dragState.startValues[valueIndex] + delta;
    applyTargetValue(dragState.target, dragState.axis, nextValue);
  };

  const onPointerUpWindow = () => {
    if (!dragState) return;
    const endedGroup = dragState.startedHistoryGroup;
    const capturedPointerId = dragState.pointerId;
    dragState = null;
    setDraggingAxis(gizmo, null);
    window.removeEventListener("pointermove", onPointerMoveWindow, true);
    window.removeEventListener("pointerup", onPointerUpWindow, true);
    window.removeEventListener("pointercancel", onPointerUpWindow, true);
    if (controlsRef?.current) controlsRef.current.enabled = true;
    rendererDom.style.cursor = hoveredAxis == null ? "" : "grab";
    if (capturedPointerId != null) {
      try {
        rendererDom.releasePointerCapture(capturedPointerId);
      } catch {
        // ignore
      }
    }
    if (endedGroup) {
      try {
        getHistoryActions().endGroup(getSceneState());
      } catch (error) {
        logger.warn("[ScaleGizmo] Failed to end history group", error);
      }
    }
  };

  window.addEventListener("pointerdown", onPointerDownWindow, true);
  rendererDom.addEventListener("pointermove", onPointerMoveCanvas);
  rendererDom.addEventListener("pointerleave", onPointerLeaveCanvas);

  const frameUpdate = () => {
    if (isDisabled()) {
      gizmo.root.visible = false;
      setDraggingAxis(gizmo, null);
      setHover(null);
      return;
    }
    const target = opts.getSelectedTarget();
    const sceneObj = target?.object || null;
    if (!target || !sceneObj) {
      gizmo.root.visible = false;
      setDraggingAxis(gizmo, null);
      setHover(null);
      return;
    }

    const worldMat = computeWorldMatrix(sceneObj);
    const transformOrigin = new THREE.Vector3().setFromMatrixPosition(worldMat);
    const origin = computeVisibleGeometryWorldCenter(sceneObj, transformOrigin);
    const frameObject =
      target.kind === "mesh" || target.meshUuid
        ? findAncestorForBodyUuid(sceneObj, target.bodyUuid) || sceneObj
        : sceneObj;
    const frameMat = computeWorldMatrix(frameObject);
    const frameQuat = new THREE.Quaternion().setFromRotationMatrix(frameMat);
    const camPos = new THREE.Vector3();
    camera.getWorldPosition(camPos);
    updateGizmoTransform(gizmo, origin, frameQuat, camPos.distanceTo(origin));
    if (!gizmo.root.visible) gizmo.root.visible = true;
  };

  const detach = () => {
    if (dragState) onPointerUpWindow();
    window.removeEventListener("pointerdown", onPointerDownWindow, true);
    rendererDom.removeEventListener("pointermove", onPointerMoveCanvas);
    rendererDom.removeEventListener("pointerleave", onPointerLeaveCanvas);
    if (rendererDom.style.cursor === "grab" || rendererDom.style.cursor === "grabbing") {
      rendererDom.style.cursor = "";
    }
    try {
      disposePositionGizmo(gizmo);
    } catch (error) {
      logger.warn("[ScaleGizmo] Failed to dispose gizmo", error);
    }
  };

  return { frameUpdate, detach };
}
